import { useEffect, useRef, useState, type HTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import './Progress.css';

export type ProgressTone = 'primary' | 'accent' | 'xp' | 'danger';

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0 to 1. Values outside are clamped. */
  value: number;
  tone?: ProgressTone;
  size?: 'sm' | 'md' | 'lg';
  /** Screen-reader label describing what the progress represents. */
  label?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * ProgressBar — horizontal pill filled to `value`. Used for lesson
 * progress pips, XP-to-next-level, paywall fill, etc.
 */
export function ProgressBar({
  value,
  tone = 'primary',
  size = 'md',
  label,
  className,
  ref,
  ...rest
}: ProgressBarProps) {
  const pct = Math.max(0, Math.min(1, value));

  // Flash only when the bar CROSSES into full while mounted — a bar that
  // mounts already at 100% (e.g. a maxed level on Profile) must not flash.
  const prevPct = useRef(pct);
  const [justCompleted, setJustCompleted] = useState(false);
  useEffect(() => {
    if (pct >= 1 && prevPct.current < 1) setJustCompleted(true);
    prevPct.current = pct;
  }, [pct]);

  return (
    <div
      ref={ref}
      className={clsx('fluent-progress', className)}
      data-tone={tone}
      data-size={size}
      data-just-completed={justCompleted || undefined}
      role="progressbar"
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      {...rest}
    >
      <div className="fluent-progress__fill" style={{ width: `${pct * 100}%` }} />
    </div>
  );
}
